import React from "react";

const record = (value) => value && typeof value === "object" && !Array.isArray(value) ? value : {};
const label = (value, fallback = "Not recorded") => typeof value === "string" && value.trim() ? value : fallback;
const quantity = (value) => Number.isSafeInteger(value) && value > 0 ? value : "Not recorded";
const money = (value, currency) => {
  if (!Number.isSafeInteger(value) || value < 0 || !/^[A-Z]{3}$/.test(currency)) return "Pending review";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(value / 100);
  } catch {
    return "Pending review";
  }
};

// Historical intake evidence only: never look up today's catalog, use client totals,
// or recalculate a price. A later quote is a separate document.
export default function CatalogSnapshot({ selection, result }) {
  if (!selection && !result) return null;
  const snapshot = record(result);
  const request = record(selection);
  const currency = label(snapshot.currency, "");
  const entry = record(snapshot.entry);
  const lines = Array.isArray(snapshot.lines) ? snapshot.lines.filter((line) => line && typeof line === "object" && !Array.isArray(line)) : [];
  const pending = Array.isArray(snapshot.pending) ? snapshot.pending.filter((item) => typeof item === "string" && item.trim()) : [];
  const manualReview = snapshot.quoteOnly !== false || snapshot.complete !== true || pending.length > 0;
  const requested = Object.entries(record(request.selections)).filter(([, count]) => Number.isSafeInteger(count) && count > 0);

  return (
    <section aria-label="Saved catalog snapshot" className="border border-sky-400/20 rounded p-4 space-y-3 text-sm text-zinc-200" data-testid="catalog-snapshot">
      <h3 className="font-semibold text-sky-300">Saved catalog snapshot (read-only)</h3>
      <p>{label(snapshot.productLabel, label(entry.label, label(request.entryId, "Catalog selection")))}</p>
      <p>{manualReview ? "Manual review required / quote-only" : "Verified configuration saved"}. Final quote pending. Booking not confirmed.</p>
      {lines.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <caption className="sr-only">Saved server-resolved assembly components</caption>
            <thead><tr>{["Selected item", "Quantity", "Unit price", "Line price"].map((heading) => <th key={heading} scope="col" className="p-2">{heading}</th>)}</tr></thead>
            <tbody>{lines.map((line, index) => (
              <tr key={index} className="border-t border-white/10">
                <th scope="row" className="p-2 font-normal">{label(line.label, label(line.id, "Unlabeled item"))}</th>
                <td className="p-2">{quantity(line.quantity)}</td>
                <td className="p-2 whitespace-nowrap">{money(line.unitAmountCents, currency)}</td>
                <td className="p-2 whitespace-nowrap">{money(line.amountCents, currency)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : (
        <div>
          <p>No server-verified priced lines recorded. Price pending manual review.</p>
          {requested.length > 0 && <ul>{requested.map(([id, count]) => <li key={id}>Requested option {id}: quantity {count}</li>)}</ul>}
        </div>
      )}
      <p className="font-semibold">Saved server subtotal (known components): {lines.length ? money(snapshot.knownSubtotalCents, currency) : "Pending review"}</p>
      <p>Currency: {currency || "Not recorded"} · Catalog version: {label(snapshot.catalogVersion)} · Inventory version: {label(snapshot.inventoryVersion)}</p>
      {pending.length > 0 && <div><p>Pending assessment:</p><ul className="list-disc pl-5">{pending.map((item, index) => <li key={index}>{item}</li>)}</ul></div>}
      <p className="text-zinc-400">Assembly components only. Site extras excluded. This saved intake snapshot is not a final quote or booking confirmation.</p>
    </section>
  );
}
